import type {
  TemplateAuthoringDraftStatus,
  TemplateAuthoringProvenance,
  TemplateAuthoringValidationIssue,
} from './admin-api';
import { templatePrimaryDisplayName } from './template-admin-presentation';
import type {
  AuthoringBindingKind,
  AuthoringCaptureMode,
  AuthoringFieldValueType,
  AuthoringOwnerCode,
  AuthoringPlacement,
  AuthoringPreviewView,
  ApplicabilityRuleType,
  RequiredRuleType,
  SnapshotPathFamily,
} from './template-authoring-model';

const PROVENANCE_LABELS: Record<TemplateAuthoringProvenance, string> = {
  MANUAL: 'Создана с нуля',
  IMPORTED: 'Импортирована из Excel',
  CLONED: 'Создана на основе существующего',
};

/** Published TemplateVersion that is not current → user-facing «Архивная». */
export function publishedVersionUserStatus(isActive: boolean): 'Активная' | 'Архивная' {
  return isActive ? 'Активная' : 'Архивная';
}

export function draftVersionRowTitle(proposedVersionCode: string | null): string {
  const code = proposedVersionCode?.trim() ?? '';
  if (code.length > 0) {
    return `Версия ${code} · Черновик`;
  }
  return 'Новая версия · Черновик';
}

const DRAFT_STATUS_LABELS: Record<TemplateAuthoringDraftStatus, string> = {
  DRAFT: 'Черновик версии',
  VALIDATED: 'Проверка пройдена',
  PUBLISHED: 'Опубликован',
  DISCARDED: 'Отброшен',
};

/** Tooltip for lifecycle UI; empty when no short explanation is needed. */
const DRAFT_STATUS_HINTS: Partial<Record<TemplateAuthoringDraftStatus, string>> = {
  VALIDATED:
    'Серверная проверка завершена без блокирующих ошибок. После изменения потребуется проверить снова.',
};

const BINDING_LABELS: Record<AuthoringBindingKind, string> = {
  CASE_SNAPSHOT: 'Из сведений об осмотре',
  CASE_DYNAMIC: 'Общее поле осмотра',
  CHECKLIST_DYNAMIC: 'Только этот чек-лист',
};

const OWNER_LABELS: Record<AuthoringOwnerCode, string> = {
  CONTRACTOR: 'Подрядчик',
  INSPECTOR: 'Инспектор',
  SYSTEM: 'Система',
};

const VALUE_TYPE_LABELS: Record<AuthoringFieldValueType, string> = {
  STRING: 'Строка',
  TEXT: 'Текст',
  NUMBER: 'Число',
  DATE: 'Дата',
  DATETIME: 'Дата и время',
  ENUM: 'Перечисление',
  REF_CONTRACTOR: 'Организация из справочника',
  REF_VEHICLE: 'Техника из справочника',
  REF_PERSON: 'Человек из справочника',
};

const PLACEMENT_LABELS: Record<AuthoringPlacement, string> = {
  HEADER: 'Заголовок',
  OBJECT_BLOCK: 'Блок объекта',
  RESULT_BLOCK: 'Блок результата',
  SIGNATURE_BLOCK: 'Блок подписи',
};

const CAPTURE_MODE_LABELS: Record<AuthoringCaptureMode, string> = {
  METADATA_PREFILL: 'Метаданные / предзаполнение',
  PRESENCE_ONLY: 'Только наличие',
  ADMIN_DECIDE: 'Решение администратора',
};

const REQUIRED_RULE_LABELS: Record<RequiredRuleType, string> = {
  ALWAYS: 'Всегда обязательно',
  WHEN_APPLICABLE: 'Обязательно, только когда используется',
  OPTIONAL: 'Необязательно',
};

const APPLICABILITY_RULE_LABELS: Record<ApplicabilityRuleType, string> = {
  ALWAYS: 'Всегда используется',
  INSPECTOR_DECLARED: 'По решению инспектора',
  FIELD_EQUALS: 'Когда поле равно значению',
  ONE_OF: 'Когда поле одно из значений',
};

const BINDING_HINTS: Record<AuthoringBindingKind, string> = {
  CASE_SNAPSHOT:
    'Берётся из уже известных сведений осмотра (госномер, VIN и т.п.). Нужный путь выбирается отдельно.',
  CASE_DYNAMIC:
    'Общее поле осмотра — значение используется на уровне всего осмотра. Ключ создаётся автоматически и не меняется при переименовании поля.',
  CHECKLIST_DYNAMIC: 'Значение относится только к этому чек-листу, а не ко всему осмотру.',
};

const VALUE_TYPE_HINTS: Record<AuthoringFieldValueType, string> = {
  STRING: 'Короткая текстовая строка.',
  TEXT: 'Длинный или многострочный текст.',
  NUMBER: 'Число. Для чисел можно задать минимум и максимум в блоке «Проверка значения».',
  DATE: 'Дата без времени.',
  DATETIME: 'Дата и время.',
  ENUM: 'Выбор из заранее заданного списка значений.',
  REF_CONTRACTOR:
    'Ссылочный тип «организация». Выбор записи из справочника в этой форме пока не реализован. Повторное использование профиля подрядчика (CIR) — отдельный механизм.',
  REF_VEHICLE:
    'Ссылочный тип «техника». Выбор записи из справочника в этой форме пока не реализован. Повторное использование профиля ТС (CIR) — отдельный механизм.',
  REF_PERSON:
    'Ссылочный тип «человек». Выбор записи из справочника в этой форме пока не реализован; общего справочника людей в первом релизе нет.',
};

export const AUTHORING_PLACEMENT_HINT =
  'Значение сохраняется, но в текущей версии приложения не подтверждено, что оно меняет расположение на экране инспектора или в PDF.';

export const AUTHORING_PDF_METADATA_LABEL_HINT =
  'Дополнительное название для PDF пока не используется при формировании документа.';

export const AUTHORING_CONTRACTOR_APPLICABILITY_HINT =
  'Для полей подрядчика условие «когда используется» кроме «всегда» сейчас может не учитываться при предзаполнении так, как ожидается.';

export const AUTHORING_VALIDATION_SECTION_HINT =
  'Ограничения вводимого значения (например, минимальная и максимальная длина для текста или диапазон для числа). Не путать с обязательностью заполнения.';

const SNAPSHOT_FAMILY_LABELS: Record<SnapshotPathFamily, string> = {
  STATIC: 'Статический путь ТС',
  VEHICLE_DATA: 'Данные ТС (vehicle.data.*)',
  CONTRACTOR_DATA: 'Данные подрядчика (contractor.data.*)',
  PERSON_FULL_NAME: 'ФИО лица',
  PERSON_DATA: 'Данные лица (person[].data.*)',
};

const PREVIEW_VIEW_LABELS: Record<AuthoringPreviewView, string> = {
  CONTRACTOR: 'Подрядчик',
  INSPECTOR: 'Инспектор',
  PDF: 'PDF / представление',
};

const SUBJECT_KIND_LABELS: Record<string, string> = {
  GENERAL: 'Общие',
  FIELD: 'Поля',
  DOCUMENT: 'Документы',
  SECTION: 'Разделы',
  ITEM: 'Пункты',
  PRESENTATION: 'Представление / PDF',
  HEADER_CANDIDATE: 'Найденные поля шапки',
};

export const UNRESOLVED_HEADER_FIELD_CANDIDATE_CODE = 'UNRESOLVED_HEADER_FIELD_CANDIDATE';

export interface UnresolvedHeaderCandidateDisplay {
  readonly label: string;
  readonly sourceSheet: string | null;
  readonly sourceRow: number | null;
  readonly candidateId: string | null;
}

/**
 * Business label + optional Excel source for UNRESOLVED_HEADER_FIELD_CANDIDATE.
 * Prefers live candidate form data; falls back to parsing the server message shape
 * `Unresolved header field candidate {label} ({sheet} row {n})`.
 */
export function unresolvedHeaderCandidateDisplay(
  issue: Pick<TemplateAuthoringValidationIssue, 'code' | 'message' | 'subject_id'>,
  candidates:
    | readonly { readonly candidate_id: string; readonly raw_label: string; readonly source_row: number }[]
    | null
    | undefined,
  sourceSheet: string | null | undefined,
): UnresolvedHeaderCandidateDisplay {
  const candidateId =
    issue.subject_id !== null && issue.subject_id.trim().length > 0 ? issue.subject_id : null;
  const fromForm =
    candidateId === null || candidates === undefined || candidates === null
      ? undefined
      : candidates.find((row) => row.candidate_id === candidateId);
  if (fromForm !== undefined) {
    return {
      label: fromForm.raw_label,
      sourceSheet:
        sourceSheet !== undefined && sourceSheet !== null && sourceSheet.trim().length > 0
          ? sourceSheet.trim()
          : null,
      sourceRow: fromForm.source_row,
      candidateId,
    };
  }
  const match = /^Unresolved header field candidate (.+) \((.+) row (\d+)\)$/.exec(
    issue.message.trim(),
  );
  if (match !== null) {
    return {
      label: match[1] ?? 'Поле шапки',
      sourceSheet: match[2] ?? null,
      sourceRow: Number(match[3]),
      candidateId,
    };
  }
  return {
    label: candidateId ?? 'Поле шапки',
    sourceSheet: null,
    sourceRow: null,
    candidateId,
  };
}

export function isUnresolvedHeaderFieldCandidateIssue(
  issue: Pick<TemplateAuthoringValidationIssue, 'code' | 'subject_kind'>,
): boolean {
  return (
    issue.code === UNRESOLVED_HEADER_FIELD_CANDIDATE_CODE ||
    issue.subject_kind === 'HEADER_CANDIDATE'
  );
}

export function authoringProvenanceLabel(value: string): string {
  return PROVENANCE_LABELS[value as TemplateAuthoringProvenance] ?? value;
}

export function authoringDraftStatusLabel(value: string): string {
  return DRAFT_STATUS_LABELS[value as TemplateAuthoringDraftStatus] ?? value;
}

export function authoringDraftStatusHint(value: string): string | null {
  return DRAFT_STATUS_HINTS[value as TemplateAuthoringDraftStatus] ?? null;
}

export function authoringBindingKindLabel(value: string): string {
  return BINDING_LABELS[value as AuthoringBindingKind] ?? value;
}

export function authoringBindingKindHint(value: string): string {
  return BINDING_HINTS[value as AuthoringBindingKind] ?? '';
}

export function authoringOwnerLabel(value: string): string {
  return OWNER_LABELS[value as AuthoringOwnerCode] ?? value;
}

export function authoringValueTypeLabel(value: string): string {
  return VALUE_TYPE_LABELS[value as AuthoringFieldValueType] ?? value;
}

export function authoringValueTypeHint(value: string): string {
  return VALUE_TYPE_HINTS[value as AuthoringFieldValueType] ?? '';
}

export function authoringPlacementLabel(value: string): string {
  return PLACEMENT_LABELS[value as AuthoringPlacement] ?? value;
}

export function authoringCaptureModeLabel(value: string): string {
  return CAPTURE_MODE_LABELS[value as AuthoringCaptureMode] ?? value;
}

export function authoringRequiredRuleLabel(value: string): string {
  return REQUIRED_RULE_LABELS[value as RequiredRuleType] ?? value;
}

export function authoringApplicabilityRuleLabel(value: string): string {
  return APPLICABILITY_RULE_LABELS[value as ApplicabilityRuleType] ?? value;
}

export function authoringSnapshotFamilyLabel(value: string): string {
  return SNAPSHOT_FAMILY_LABELS[value as SnapshotPathFamily] ?? value;
}

export function authoringPreviewViewLabel(value: string): string {
  return PREVIEW_VIEW_LABELS[value as AuthoringPreviewView] ?? value;
}

export function authoringSubjectKindLabel(value: string): string {
  return SUBJECT_KIND_LABELS[value] ?? value;
}

export function authoringDraftBusinessLabel(draft: {
  readonly template_code: string;
  readonly template_name: string;
}): string {
  const raw = draft.template_name.trim().length > 0 ? draft.template_name : draft.template_code;
  return templatePrimaryDisplayName(raw);
}

/** Stored template name before primary-display demotion (for technical popover). */
export function authoringDraftStoredTemplateName(draft: {
  readonly template_code: string;
  readonly template_name: string;
}): string {
  return draft.template_name.trim().length > 0 ? draft.template_name.trim() : draft.template_code;
}

export function formatAuthoringTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('ru-RU');
}

export function documentTypeBusinessLabel(documentType: {
  readonly code: string;
  readonly display_name: string;
}): string {
  return `${documentType.code} — ${documentType.display_name}`;
}

export function templateVersionBusinessLabel(version: {
  readonly version_code: string;
  readonly template_code?: string;
  readonly is_active?: boolean;
}): string {
  const base =
    version.template_code === undefined || version.template_code.length === 0
      ? version.version_code
      : `${version.template_code} · ${version.version_code}`;
  if (version.is_active === true) {
    return `${base} (активна)`;
  }
  return base;
}

export function isAuthoringPublishReady(status: string, errorCount: number): boolean {
  return errorCount === 0 && (status === 'VALIDATED' || status === 'DRAFT');
}

export function validationIssueArea(
  issue: Pick<TemplateAuthoringValidationIssue, 'subject_kind'>,
): string {
  return authoringSubjectKindLabel(issue.subject_kind);
}

export function canEditAuthoringDraft(status: string): boolean {
  return status === 'DRAFT' || status === 'VALIDATED';
}

export function canDiscardAuthoringDraft(status: string): boolean {
  return status === 'DRAFT' || status === 'VALIDATED';
}

/** Normal Constructor list shows only editable operational drafts. */
export function isOperationalAuthoringDraftStatus(status: string): boolean {
  return status === 'DRAFT' || status === 'VALIDATED';
}

/**
 * Compact Constructor checklist-row meta. Does not merge roots by display name.
 * Version codes come from catalog DTO fields only (no per-row versions fetch).
 */
export function constructorChecklistRowMeta(
  template: {
    readonly active_version_code: string | null;
    readonly has_approved_version?: boolean;
    readonly active_template_version_id: string | null;
  },
  hasOperationalDraft: boolean,
): string {
  const parts: string[] = [];
  const activeCode =
    template.active_version_code !== null && template.active_version_code.trim().length > 0
      ? template.active_version_code.trim()
      : null;
  if (activeCode !== null) {
    parts.push(`Активная версия: ${activeCode}`);
  } else if (
    template.has_approved_version === true ||
    template.active_template_version_id !== null
  ) {
    parts.push('Есть опубликованные версии · Активная версия не выбрана');
  } else {
    parts.push('Нет опубликованной версии');
  }
  if (hasOperationalDraft) {
    parts.push('Есть черновик');
  }
  return parts.join(' · ');
}

export function authoringSubjectBindingLabel(value: string | null | undefined): string {
  if (value === 'VEHICLE') {
    return 'Транспортное средство';
  }
  if (value === 'DRIVER' || value === 'PERSON_DRIVER') {
    return 'Водитель / персонал';
  }
  if (value === 'PERSON_OPERATOR') {
    return 'Машинист / оператор';
  }
  if (value === 'CONTRACTOR') {
    return 'Подрядчик';
  }
  if (value === 'EQUIPMENT') {
    return 'Оборудование';
  }
  if (value === 'ORGANIZATION') {
    return 'Организация';
  }
  if (value === 'ORGANIZATION_EQUIPMENT') {
    return 'Организация / оборудование';
  }
  if (value === null || value === undefined || value.trim().length === 0) {
    return 'Не определён';
  }
  return value;
}

export function editorAreaForSubjectKind(subjectKind: string): string {
  switch (subjectKind) {
    case 'FIELD':
    case 'HEADER_CANDIDATE':
      return 'fields';
    case 'DOCUMENT':
      return 'documents';
    case 'SECTION':
    case 'ITEM':
      return 'sections';
    case 'PRESENTATION':
      return 'presentation';
    default:
      return 'general';
  }
}

/** Short hint above add-field search results (TASK-026). */
export const FIELD_REUSE_ADD_HINT =
  'Если одинаковые сведения нужны в нескольких чек-листах, свяжите поля — их не придётся заполнять повторно в одном осмотре';

export const FIELD_REUSE_CREATE_NEW_CAPTION = 'Без связи с существующими полями';

export const FIELD_REUSE_LINKED_HINT =
  'Если эти чек-листы входят в один осмотр, сведения заполняются один раз';

export const FIELD_REUSE_IDENTITY_EDIT_HINT =
  'Сначала разорвите связь, чтобы изменить тип, ответственного или условия заполнения общего поля.';

export function fieldReuseUsedInSummary(
  usedIn: readonly { readonly template_name: string; readonly template_code: string }[],
): string {
  if (usedIn.length === 0) {
    return '';
  }
  const labels = usedIn.map((row) => fieldReuseDisplayTemplateLabel(row));
  if (labels.every((label) => label === 'Чек-лист')) {
    return usedIn.length === 1 ? '1 чек-лист' : `${usedIn.length} чек-листов`;
  }
  return labels.join(', ');
}

/**
 * Compact linked-row status. Never inlines technical template codes/names.
 * N = distinct external ACTIVE used_in already scoped by exclude on the server.
 */
export function fieldReuseLinkedStatusLine(
  usedIn: readonly { readonly template_name: string; readonly template_code: string }[],
): string {
  const count = usedIn.length;
  if (count < 1) {
    return 'Общее поле';
  }
  return `Общее поле · Связано с ${count} другими чек-листами`;
}

/**
 * Popover / list label: prefer human template_name; never lead with machine codes.
 * Technical/test names → neutral «Чек-лист» (code only as tooltip/title elsewhere).
 */
export function fieldReuseDisplayTemplateLabel(row: {
  readonly template_name: string;
  readonly template_code: string;
}): string {
  const name = row.template_name.trim();
  const code = row.template_code.trim();
  if (name.length < 1) {
    return 'Чек-лист';
  }
  if (isMachineTemplateLabel(name) || (code.length > 0 && name === code)) {
    return 'Чек-лист';
  }
  return name;
}

function isMachineTemplateLabel(value: string): boolean {
  if (/^REAL-TEMPLATE/i.test(value)) {
    return true;
  }
  // Long hyphenated uppercase/digit codes typical of fixture templates.
  if (value.length >= 18 && /^[A-Z0-9]+(?:-[A-Z0-9]+)+$/.test(value)) {
    return true;
  }
  return false;
}
